import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

export default function OrgTab() {
  const { can } = useAuth();
  const canManage = can("hr:employee_manage");
  const [depts, setDepts] = useState([]);
  const [positions, setPositions] = useState([]);
  const [dOpen, setDOpen] = useState(false);
  const [dEdit, setDEdit] = useState(null);
  const [dForm, setDForm] = useState({ name: "", parent_id: null, kind: "team", order: 0, active: true });
  const [pOpen, setPOpen] = useState(false);
  const [pEdit, setPEdit] = useState(null);
  const [pForm, setPForm] = useState({ title: "", department_id: null, order: 0, active: true });

  const load = () => {
    api.get("/hr/departments").then(r => setDepts(r.data)).catch(() => {});
    api.get("/hr/positions").then(r => setPositions(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const divisions = depts.filter(d => !d.parent_id);
  const childrenOf = (id) => depts.filter(d => d.parent_id === id);

  const saveDept = async () => {
    try {
      if (dEdit) await api.put(`/hr/departments/${dEdit.id}`, dForm);
      else await api.post("/hr/departments", dForm);
      toast.success("Saved"); setDOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delDept = async (id) => { if (!window.confirm("Delete department?")) return;
    try { await api.delete(`/hr/departments/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const savePos = async () => {
    try {
      if (pEdit) await api.put(`/hr/positions/${pEdit.id}`, pForm);
      else await api.post("/hr/positions", pForm);
      toast.success("Saved"); setPOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delPos = async (id) => { if (!window.confirm("Delete position?")) return;
    try { await api.delete(`/hr/positions/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="grid grid-cols-2 gap-6" data-testid="hr-org-tab">
      {/* Departments tree */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">Organisation Structure</h3>
          {canManage && <Button size="sm" variant="outline" data-testid="dept-new-button" onClick={() => { setDEdit(null); setDForm({ name: "", parent_id: null, kind: "division", order: divisions.length, active: true }); setDOpen(true); }}><Plus className="w-3.5 h-3.5 mr-1" />Node</Button>}
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-1" data-testid="dept-tree">
          {divisions.map(div => (
            <div key={div.id}>
              <div className="flex items-center justify-between px-2 py-1.5 rounded bg-slate-50 font-medium text-sm" data-testid={`dept-${div.id}`}>
                <span>{div.name}</span>
                {canManage && <span className="flex gap-1">
                  <button onClick={() => { setDEdit(div); setDForm(div); setDOpen(true); }}><Pencil className="w-3.5 h-3.5 text-slate-400" /></button>
                  <button onClick={() => delDept(div.id)}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></button>
                </span>}
              </div>
              {childrenOf(div.id).map(c => (
                <div key={c.id} className="flex items-center justify-between pl-6 pr-2 py-1 text-sm text-slate-600" data-testid={`dept-${c.id}`}>
                  <span>— {c.name}</span>
                  {canManage && <span className="flex gap-1">
                    <button onClick={() => { setDEdit(c); setDForm(c); setDOpen(true); }}><Pencil className="w-3.5 h-3.5 text-slate-400" /></button>
                    <button onClick={() => delDept(c.id)}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></button>
                  </span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Positions */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">Positions</h3>
          {canManage && <Button size="sm" variant="outline" data-testid="pos-new-button" onClick={() => { setPEdit(null); setPForm({ title: "", department_id: null, order: positions.length, active: true }); setPOpen(true); }}><Plus className="w-3.5 h-3.5 mr-1" />Position</Button>}
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-1" data-testid="positions-list">
          {positions.map(p => (
            <div key={p.id} className="flex items-center justify-between px-2 py-1.5 text-sm" data-testid={`pos-${p.id}`}>
              <span>{p.title} {p.department_id && <span className="text-slate-400 text-xs">· {depts.find(d => d.id === p.department_id)?.name}</span>}</span>
              {canManage && <span className="flex gap-1">
                <button onClick={() => { setPEdit(p); setPForm(p); setPOpen(true); }}><Pencil className="w-3.5 h-3.5 text-slate-400" /></button>
                <button onClick={() => delPos(p.id)}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></button>
              </span>}
            </div>
          ))}
          {positions.length === 0 && <div className="text-slate-400 text-sm text-center py-4">No positions yet.</div>}
        </div>
      </div>

      {/* Dept dialog */}
      <Dialog open={dOpen} onOpenChange={setDOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dEdit ? "Edit node" : "New node"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="dept-name-input" value={dForm.name} onChange={e => setDForm({ ...dForm, name: e.target.value })} /></div>
            <div><Label>Kind</Label>
              <Select value={dForm.kind} onValueChange={v => setDForm({ ...dForm, kind: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["division", "department", "team"].map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Parent (leave empty for top-level)</Label>
              <Select value={dForm.parent_id || "none"} onValueChange={v => setDForm({ ...dForm, parent_id: v === "none" ? null : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— Top level —</SelectItem>{depts.filter(d => d.id !== dEdit?.id).map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDOpen(false)}>Cancel</Button><Button onClick={saveDept} data-testid="dept-save-button" className="bg-slate-900">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Position dialog */}
      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{pEdit ? "Edit position" : "New position"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input data-testid="pos-title-input" value={pForm.title} onChange={e => setPForm({ ...pForm, title: e.target.value })} /></div>
            <div><Label>Department (optional)</Label>
              <Select value={pForm.department_id || "none"} onValueChange={v => setPForm({ ...pForm, department_id: v === "none" ? null : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{depts.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPOpen(false)}>Cancel</Button><Button onClick={savePos} data-testid="pos-save-button" className="bg-slate-900">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
